
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { apiFetch } from '@/api';
import { Sparkles, AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

/**
 * Compute a lightweight fingerprint of the data so we only re-fetch AI insights
 * when the actual rows change (i.e. cross-filter or data refresh with new data),
 * not on every re-render.
 */
function dataFingerprint(data: any[]): string {
    if (!data || data.length === 0) return '';
    // Use first row keys + first 5 rows of values as a cheap hash
    const sample = data.slice(0, 5);
    return JSON.stringify(sample.map(row => {
        const clean = { ...row };
        delete clean.x_val;
        delete clean.extras;
        return clean;
    }));
}

export const WidgetInsights = ({ widget, data, onClose }) => {
    const [insights, setInsights] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Track the fingerprint of the last data we fetched insights for.
    // Insights are only re-fetched when the fingerprint changes.
    const lastFingerprintRef = useRef<string>('');

    const fingerprint = useMemo(() => dataFingerprint(data), [data]);

    useEffect(() => {
        // Skip if data is empty or fingerprint hasn't changed
        if (!fingerprint || fingerprint === lastFingerprintRef.current) return;

        const fetchInsights = async () => {
            setLoading(true);
            setError(null);

            try {
                const summaryData = data.slice(0, 5).map(row => {
                    const cleanRow = { ...row };
                    delete cleanRow.x_val;
                    delete cleanRow.extras;
                    return cleanRow;
                });

                const dataString = summaryData.map(row =>
                    Object.entries(row).map(([k, v]) => `${k}: ${v}`).join(', ')
                ).join('\n');

                const prompt = `
You are a Data Storyteller. Analyze the following ${widget.type} chart titled "${widget.config?.title || 'Untitled'}".
The user wants to understand the key takeaways immediately.

Data:
${dataString}

Task:
1. Identify the most significant insight (e.g., highest value, alarming trend, outlier).
2. Explain *why* it might be happening based on the data context (or state what is driving it).
3. Provide a "Wow" summary.

Formatting:
- Use bullet points.
- Use **bold** for key numbers.
- Keep it under 50 words per bullet.
- Be enthusiastic but professional.
                `;

                const response = await apiFetch('/ai/completion', null, {
                    method: 'POST',
                    body: JSON.stringify({
                        prompt: prompt,
                        model: 'gpt-4o-mini'
                    })
                });

                if (response && response.content) {
                    setInsights(response.content);
                    lastFingerprintRef.current = fingerprint;
                } else {
                    setError("No insights generated.");
                }

            } catch (err) {
                console.error("AI Insight Error:", err);
                setError("Failed to generate insights.");
            } finally {
                setLoading(false);
            }
        };

        fetchInsights();
    }, [fingerprint]); // Only re-run when the data fingerprint changes

    return (
        <div className="shrink-0 w-full px-3 pt-2 pb-1.5 bg-gradient-to-r from-purple-50 to-violet-50 border-b border-purple-100">
            <div className="flex items-start gap-2">
                {/* Header */}
                <div className="flex items-center gap-1.5 text-purple-700 font-semibold text-[11px] uppercase tracking-wider shrink-0 pt-0.5">
                    <Sparkles className="w-3.5 h-3.5" />
                    AI Key Findings
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    {loading && (
                        <div className="flex items-center gap-1.5 text-[11px] text-purple-500 animate-pulse">
                            <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                            Analyzing...
                        </div>
                    )}

                    {error && !loading && (
                        <div className="flex items-center gap-1 text-[11px] text-red-500">
                            <AlertCircle className="w-3 h-3 shrink-0" />
                            {error}
                        </div>
                    )}

                    {insights && !loading && (
                        <div className="prose prose-sm max-w-none text-[11px] leading-snug text-slate-600 overflow-hidden line-clamp-3">
                            <ReactMarkdown>{insights}</ReactMarkdown>
                        </div>
                    )}
                </div>

                {/* Close */}
                <button
                    onClick={onClose}
                    className="shrink-0 text-slate-400 hover:text-slate-600 p-0.5 rounded hover:bg-slate-100 transition-colors"
                    title="Close AI Findings"
                >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>
    );
};
